/**
 * 整库导入 / 导出。
 *
 * - 导出：把五张表（含 memories）序列化成一份带版本号的 JSON（纯数据，无函数）。
 * - 导入：先做整体格式 / 版本校验，再逐条做字段级校验 + normalize；任何一条记录非法都会
 *   让整次导入原子失败（校验在写入之前完成，不产生部分写入）；
 *   冲突仍按 id 幂等覆盖（`bulkPut` 语义），重复导入同一份文件结果一致。
 */

import { getDb, type DatabaseSnapshot, type TABLE_NAMES } from '../db';
import { parseRoleModelConfig } from '../roleModel';
import type {
  Attachment,
  ChatMessage,
  Conversation,
  MemoryEntry,
  ModelSettings,
  Preset,
  ProviderConfig,
  Role,
  WorldBookEntry,
  WorldBookSelectiveLogic,
} from '../types';

export const EXPORT_FORMAT = 'zxtalk.backup';
export const EXPORT_VERSION = 1;

export interface BackupFile extends DatabaseSnapshot {
  format: typeof EXPORT_FORMAT;
  version: number;
  exportedAt: number;
}

/** 导入结果：每张表实际写入的条数（含 T13c 并入备份的 memories）。 */
export type ImportCounts = Record<(typeof TABLE_NAMES)[number] | 'memories', number>;

function emptySnapshot(): DatabaseSnapshot {
  return { conversations: [], messages: [], presets: [], providers: [], memories: [] };
}

/** 导出整库为可序列化对象。 */
export async function exportDatabase(): Promise<BackupFile> {
  const db = getDb();
  const [conversations, messages, presets, providers, memories] = await Promise.all([
    db.conversations.toArray(),
    db.messages.toArray(),
    db.presets.toArray(),
    db.providers.toArray(),
    db.memories.toArray(),
  ]);

  return {
    format: EXPORT_FORMAT,
    version: EXPORT_VERSION,
    exportedAt: Date.now(),
    conversations,
    messages,
    presets: presets.map(preset => ({ ...preset, ...(preset.modelConfig === undefined ? {} : { modelConfig: parseRoleModelConfig(preset.modelConfig) }) })),
    providers,
    memories,
  };
}

/** 导出整库为 JSON 字符串。 */
export async function exportDatabaseJson(space = 2): Promise<string> {
  return JSON.stringify(await exportDatabase(), null, space);
}

export class ImportFormatError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ImportFormatError';
  }
}

const ROLES: readonly Role[] = ['system', 'user', 'assistant', 'tool'];
const MESSAGE_STATUSES: readonly ChatMessage['status'][] = [
  'pending',
  'streaming',
  'done',
  'error',
];
const ATTACHMENT_KINDS: readonly Attachment['kind'][] = ['image', 'file', 'audio'];
const PROVIDER_KINDS: readonly ProviderConfig['kind'][] = [
  'openai',
  'anthropic',
  'gemini',
  'custom',
];
const PROVIDER_PURPOSES: readonly NonNullable<ProviderConfig['purpose']>[] = ['chat', 'image', 'tts'];
const PROVIDER_PROTOCOLS: readonly NonNullable<ProviderConfig['protocol']>[] = ['chat-completions', 'responses', 'anthropic', 'gemini'];
const PRESET_SOURCES: readonly NonNullable<Preset['source']>[] = ['local', 'community'];
const SELECTIVE_LOGICS: readonly WorldBookSelectiveLogic[] = ['andAny', 'andAll', 'notAny', 'notAll'];
const THINKING_LEVELS: readonly ModelSettings['thinkingLevel'][] = [
  'off',
  'low',
  'medium',
  'high',
];
const MODEL_KINDS: readonly NonNullable<Conversation['modelKind']>[] = ['text', 'image'];

/** 抛出一处可定位的错误：path 已经包含「表名[记录序号].字段名」。 */
function fail(path: string, reason: string): never {
  throw new ImportFormatError(`${path}：${reason}`);
}

function typeName(value: unknown): string {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  return typeof value;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function requireObject(value: unknown, path: string): Record<string, unknown> {
  if (!isRecord(value)) {
    fail(path, `字段类型不符，期望 object，实际为 ${typeName(value)}`);
  }
  return value;
}

function requireArray(value: unknown, path: string): unknown[] {
  if (!Array.isArray(value)) {
    fail(path, `字段类型不符，期望 array，实际为 ${typeName(value)}`);
  }
  return value;
}

function requireString(
  source: Record<string, unknown>,
  field: string,
  path: string,
): string {
  const value = source[field];
  if (value === undefined || value === null) {
    fail(`${path}.${field}`, '字段缺失，期望 string');
  }
  if (typeof value !== 'string') {
    fail(`${path}.${field}`, `字段类型不符，期望 string，实际为 ${typeName(value)}`);
  }
  return value;
}

function optionalString(
  source: Record<string, unknown>,
  field: string,
  path: string,
): string | undefined {
  const value = source[field];
  if (value === undefined) return undefined;
  if (typeof value !== 'string') {
    fail(`${path}.${field}`, `字段类型不符，期望 string，实际为 ${typeName(value)}`);
  }
  return value;
}

function requireNumber(
  source: Record<string, unknown>,
  field: string,
  path: string,
): number {
  const value = source[field];
  if (value === undefined || value === null) {
    fail(`${path}.${field}`, '字段缺失，期望 number');
  }
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    fail(`${path}.${field}`, `字段类型不符，期望 number，实际为 ${typeName(value)}`);
  }
  return value;
}

function requireBoolean(
  source: Record<string, unknown>,
  field: string,
  path: string,
): boolean {
  const value = source[field];
  if (value === undefined || value === null) {
    fail(`${path}.${field}`, '字段缺失，期望 boolean');
  }
  if (typeof value !== 'boolean') {
    fail(`${path}.${field}`, `字段类型不符，期望 boolean，实际为 ${typeName(value)}`);
  }
  return value;
}

function requireEnum<T extends string>(
  source: Record<string, unknown>,
  field: string,
  allowed: readonly T[],
  path: string,
): T {
  const value = requireString(source, field, path);
  if (!(allowed as readonly string[]).includes(value)) {
    fail(`${path}.${field}`, `取值非法，允许 ${allowed.join(' | ')}，实际为 ${value}`);
  }
  return value as T;
}

function optionalEnum<T extends string>(
  source: Record<string, unknown>,
  field: string,
  allowed: readonly T[],
  path: string,
): T | undefined {
  const value = optionalString(source, field, path);
  if (value === undefined) return undefined;
  if (!(allowed as readonly string[]).includes(value)) {
    fail(`${path}.${field}`, `取值非法，允许 ${allowed.join(' | ')}，实际为 ${value}`);
  }
  return value as T;
}

function normalizeStringArray(value: unknown, path: string): string[] {
  return requireArray(value, path).map((item, index) => {
    if (typeof item !== 'string') {
      fail(`${path}[${index}]`, `字段类型不符，期望 string，实际为 ${typeName(item)}`);
    }
    return item;
  });
}

function normalizeAttachment(value: unknown, path: string): Attachment {
  const source = requireObject(value, path);
  const attachment: Attachment = {
    id: requireString(source, 'id', path),
    kind: requireEnum(source, 'kind', ATTACHMENT_KINDS, path),
    name: requireString(source, 'name', path),
    mime: requireString(source, 'mime', path),
    size: requireNumber(source, 'size', path),
  };
  const dataUrl = optionalString(source, 'dataUrl', path);
  if (dataUrl !== undefined) attachment.dataUrl = dataUrl;
  return attachment;
}

function normalizeAttachments(value: unknown, path: string): Attachment[] {
  return requireArray(value, path).map((item, index) =>
    normalizeAttachment(item, `${path}[${index}]`),
  );
}

function normalizeModelSettings(value: unknown, path: string): ModelSettings {
  const source = requireObject(value, path);
  const settings: ModelSettings = {
    maxContext: requireNumber(source, 'maxContext', path),
    thinkingLevel: requireEnum(source, 'thinkingLevel', THINKING_LEVELS, path),
    temperature: requireNumber(source, 'temperature', path),
    topP: requireNumber(source, 'topP', path),
    maxOutputTokens: requireNumber(source, 'maxOutputTokens', path),
    stream: requireBoolean(source, 'stream', path),
  };
  const systemPrompt = optionalString(source, 'systemPrompt', path);
  if (systemPrompt !== undefined) settings.systemPrompt = systemPrompt;
  return settings;
}

function normalizeWorldBookEntry(value: unknown, path: string): WorldBookEntry {
  const source = requireObject(value, path);
  const name = optionalString(source, 'name', path);
  const selectiveLogic = optionalEnum(source, 'selectiveLogic', SELECTIVE_LOGICS, path);
  return {
    id: requireString(source, 'id', path),
    keywords: normalizeStringArray(source.keywords, `${path}.keywords`),
    content: requireString(source, 'content', path),
    enabled: requireBoolean(source, 'enabled', path),
    priority: requireNumber(source, 'priority', path),
    ...(source.constant === undefined ? {} : { constant: requireBoolean(source, 'constant', path) }),
    ...(name === undefined ? {} : { name }),
    ...(selectiveLogic === undefined ? {} : { selectiveLogic }),
    ...(source.secondaryKeywords === undefined ? {} : { secondaryKeywords: normalizeStringArray(source.secondaryKeywords, `${path}.secondaryKeywords`) }),
    ...(source.caseSensitive === undefined ? {} : { caseSensitive: requireBoolean(source, 'caseSensitive', path) }),
    ...(source.matchWholeWords === undefined ? {} : { matchWholeWords: requireBoolean(source, 'matchWholeWords', path) }),
  };
}

function normalizeWorldBook(value: unknown, path: string): WorldBookEntry[] {
  return requireArray(value, path).map((item, index) =>
    normalizeWorldBookEntry(item, `${path}[${index}]`),
  );
}

function normalizeConversation(value: unknown, index: number): Conversation {
  const path = `conversations[${index}]`;
  const source = requireObject(value, path);
  const conversation: Conversation = {
    id: requireString(source, 'id', path),
    title: requireString(source, 'title', path),
    createdAt: requireNumber(source, 'createdAt', path),
    updatedAt: requireNumber(source, 'updatedAt', path),
    settings: normalizeModelSettings(source.settings, `${path}.settings`),
    messageCount: requireNumber(source, 'messageCount', path),
  };
  const presetId = optionalString(source, 'presetId', path);
  if (presetId !== undefined) conversation.presetId = presetId;
  const providerId = optionalString(source, 'providerId', path);
  if (providerId !== undefined) conversation.providerId = providerId;
  const model = optionalString(source, 'model', path);
  if (model !== undefined) conversation.model = model;
  const modelKind = optionalEnum(source, 'modelKind', MODEL_KINDS, path);
  if (modelKind !== undefined) conversation.modelKind = modelKind;
  return conversation;
}

function normalizeMessage(value: unknown, index: number): ChatMessage {
  const path = `messages[${index}]`;
  const source = requireObject(value, path);
  const message: ChatMessage = {
    id: requireString(source, 'id', path),
    conversationId: requireString(source, 'conversationId', path),
    role: requireEnum(source, 'role', ROLES, path),
    content: requireString(source, 'content', path),
    createdAt: requireNumber(source, 'createdAt', path),
    status: requireEnum(source, 'status', MESSAGE_STATUSES, path),
  };
  const reasoning = optionalString(source, 'reasoning', path);
  if (reasoning !== undefined) message.reasoning = reasoning;
  if (source.attachments !== undefined) {
    message.attachments = normalizeAttachments(source.attachments, `${path}.attachments`);
  }
  if (source.images !== undefined) {
    message.images = normalizeStringArray(source.images, `${path}.images`);
  }
  const audioUrl = optionalString(source, 'audioUrl', path);
  if (audioUrl !== undefined) message.audioUrl = audioUrl;
  if (source.readAt !== undefined) message.readAt = requireNumber(source, 'readAt', path);
  if (source.backgroundJob !== undefined) {
    message.backgroundJob = normalizeBackgroundJob(source.backgroundJob, `${path}.backgroundJob`);
  }
  if (source.imageJob !== undefined) {
    message.imageJob = normalizeImageJob(source.imageJob, `${path}.imageJob`);
  }
  return message;
}

function normalizeBackgroundJob(
  value: unknown,
  path: string,
): NonNullable<ChatMessage['backgroundJob']> {
  const source = requireObject(value, path);
  const backgroundJob: NonNullable<ChatMessage['backgroundJob']> = {
    id: requireString(source, 'id', path),
    userId: requireString(source, 'userId', path),
    provider: normalizeProviderValue(source.provider, `${path}.provider`),
    model: requireString(source, 'model', path),
    settings: normalizeModelSettings(source.settings, `${path}.settings`),
  };
  if (source.saved !== undefined) backgroundJob.saved = requireBoolean(source, 'saved', path);
  return backgroundJob;
}

function normalizeImageJob(value: unknown, path: string): NonNullable<ChatMessage['imageJob']> {
  const source = requireObject(value, path);
  const serverModel = source.serverModel;
  if (serverModel !== null && typeof serverModel !== 'string') {
    fail(`${path}.serverModel`, '字段类型不符，期望 string 或 null');
  }
  return {
    prompt: requireString(source, 'prompt', path),
    providerId: requireString(source, 'providerId', path),
    model: requireString(source, 'model', path),
    serverModel,
    createdAt: requireNumber(source, 'createdAt', path),
  };
}

function normalizePreset(value: unknown, index: number): Preset {
  const path = `presets[${index}]`;
  const source = requireObject(value, path);
  const preset: Preset = {
    id: requireString(source, 'id', path),
    name: requireString(source, 'name', path),
    systemPrompt: requireString(source, 'systemPrompt', path),
  };
  if (source.modelConfig !== undefined) {
    try { preset.modelConfig = parseRoleModelConfig(source.modelConfig); }
    catch (error) { fail(`${path}.modelConfig`, error instanceof Error ? error.message : '模型配置无效'); }
  }
  const avatar = optionalString(source, 'avatar', path);
  if (avatar !== undefined) preset.avatar = avatar;
  const description = optionalString(source, 'description', path);
  if (description !== undefined) preset.description = description;
  if (source.worldBook !== undefined) {
    preset.worldBook = normalizeWorldBook(source.worldBook, `${path}.worldBook`);
  }
  if (source.tags !== undefined) {
    preset.tags = normalizeStringArray(source.tags, `${path}.tags`);
  }
  const presetSource = optionalEnum(source, 'source', PRESET_SOURCES, path);
  if (presetSource !== undefined) preset.source = presetSource;
  const automationMode = optionalEnum(source, 'automationMode', ['reply_only', 'auto_bubble', 'auto_moment', 'auto_bubble_moment'] as const, path);
  if (automationMode !== undefined) preset.automationMode = automationMode;
  if (source.autoImageEnabled !== undefined) preset.autoImageEnabled = requireBoolean(source, 'autoImageEnabled', path);
  const imageGenerationPolicy = optionalEnum(source, 'imageGenerationPolicy', ['user_priority', 'character_priority', 'balanced'] as const, path);
  if (imageGenerationPolicy !== undefined) preset.imageGenerationPolicy = imageGenerationPolicy;
  const ttsGenerationPolicy = optionalEnum(source, 'ttsGenerationPolicy', ['user_priority', 'character_priority', 'balanced'] as const, path);
  if (ttsGenerationPolicy !== undefined) preset.ttsGenerationPolicy = ttsGenerationPolicy;
  if (source.autoTtsEnabled !== undefined) preset.autoTtsEnabled = requireBoolean(source, 'autoTtsEnabled', path);
  for (const field of ['imageProviderId', 'imageModel', 'ttsProviderId', 'ttsModel'] as const) {
    const value = optionalString(source, field, path);
    if (value !== undefined) preset[field] = value;
  }
  const ttsVoiceMode = optionalEnum(source, 'ttsVoiceMode', ['preset', 'design', 'clone'] as const, path);
  if (ttsVoiceMode !== undefined) preset.ttsVoiceMode = ttsVoiceMode;
  if (source.ttsAutoVoiceDesign !== undefined) preset.ttsAutoVoiceDesign = requireBoolean(source, 'ttsAutoVoiceDesign', path);
  if (source.quietPeriods !== undefined) {
    preset.quietPeriods = requireArray(source.quietPeriods, `${path}.quietPeriods`).map((item, index) => {
      const quietPath = `${path}.quietPeriods[${index}]`;
      const period = requireObject(item, quietPath);
      return {
        id: requireString(period, 'id', quietPath),
        start: requireString(period, 'start', quietPath),
        end: requireString(period, 'end', quietPath),
      };
    });
  }
  return preset;
}

function normalizeProvider(value: unknown, index: number): ProviderConfig {
  return normalizeProviderValue(value, `providers[${index}]`);
}

function normalizeProviderValue(value: unknown, path: string): ProviderConfig {
  const source = requireObject(value, path);
  const provider: ProviderConfig = {
    id: requireString(source, 'id', path),
    kind: requireEnum(source, 'kind', PROVIDER_KINDS, path),
    name: requireString(source, 'name', path),
    baseUrl: requireString(source, 'baseUrl', path),
    apiKeyRef: requireString(source, 'apiKeyRef', path),
    enabled: requireBoolean(source, 'enabled', path),
  };
  const defaultModel = optionalString(source, 'defaultModel', path);
  if (defaultModel !== undefined) provider.defaultModel = defaultModel;
  const purpose = optionalEnum(source, 'purpose', PROVIDER_PURPOSES, path);
  if (purpose !== undefined) provider.purpose = purpose;
  const protocol = optionalEnum(source, 'protocol', PROVIDER_PROTOCOLS, path);
  if (protocol !== undefined) provider.protocol = protocol;
  const vendorId = optionalString(source, 'vendorId', path);
  if (vendorId !== undefined) provider.vendorId = vendorId;
  const logo = optionalString(source, 'logo', path);
  if (logo !== undefined) provider.logo = logo;
  return provider;
}

function normalizeMemory(value: unknown, index: number): MemoryEntry {
  const path = `memories[${index}]`;
  const source = requireObject(value, path);
  const importance = requireNumber(source, 'importance', path);
  if (!Number.isInteger(importance) || importance < 1 || importance > 5) {
    fail(`${path}.importance`, `取值非法，允许 1 | 2 | 3 | 4 | 5，实际为 ${importance}`);
  }
  const memory: MemoryEntry = {
    id: requireString(source, 'id', path),
    content: requireString(source, 'content', path),
    tags: normalizeStringArray(source.tags, `${path}.tags`),
    importance: importance as MemoryEntry['importance'],
    createdAt: requireNumber(source, 'createdAt', path),
    updatedAt: requireNumber(source, 'updatedAt', path),
    recallCount: requireNumber(source, 'recallCount', path),
  };
  const sourceConversationId = optionalString(source, 'sourceConversationId', path);
  if (sourceConversationId !== undefined) memory.sourceConversationId = sourceConversationId;
  if (source.lastRecalledAt !== undefined) {
    memory.lastRecalledAt = requireNumber(source, 'lastRecalledAt', path);
  }
  return memory;
}

/**
 * 把任意输入规范成一份完整快照。
 * - 整体：格式标识 / 版本号校验，缺表按空数组处理；
 * - 逐条：字段级校验 + normalize；错误信息形如 `messages[2].content：…`，
 *   可直接定位到记录序号与字段名。
 */
export function normalizeSnapshot(input: unknown): DatabaseSnapshot {
  const source = requireObject(input, '导入内容');

  const format = source.format;
  if (format !== undefined) {
    if (typeof format !== 'string') {
      fail('format', `字段类型不符，期望 string，实际为 ${typeName(format)}`);
    }
    if (format !== EXPORT_FORMAT) {
      throw new ImportFormatError(`未知的备份格式：${format}`);
    }
  }

  const version = source.version;
  if (version !== undefined) {
    if (typeof version !== 'number' || !Number.isFinite(version)) {
      fail('version', `字段类型不符，期望 number，实际为 ${typeName(version)}`);
    }
    if (version > EXPORT_VERSION) {
      throw new ImportFormatError(`备份版本 ${version} 高于当前支持的 ${EXPORT_VERSION}`);
    }
  }

  const snapshot = emptySnapshot();
  if (source.conversations !== undefined && source.conversations !== null) {
    snapshot.conversations = requireArray(source.conversations, 'conversations').map(
      normalizeConversation,
    );
  }
  if (source.messages !== undefined && source.messages !== null) {
    snapshot.messages = requireArray(source.messages, 'messages').map(normalizeMessage);
  }
  if (source.presets !== undefined && source.presets !== null) {
    snapshot.presets = requireArray(source.presets, 'presets').map(normalizePreset);
  }
  if (source.providers !== undefined && source.providers !== null) {
    snapshot.providers = requireArray(source.providers, 'providers').map(normalizeProvider);
  }
  if (source.memories !== undefined && source.memories !== null) {
    snapshot.memories = requireArray(source.memories, 'memories').map(normalizeMemory);
  }
  return snapshot;
}

/**
 * 导入一份快照：先整体校验 + 逐条 normalize，再在同一事务里写入。
 * 冲突按 id 覆盖（幂等）；校验失败时整次导入原子失败，不产生部分写入。
 * 返回每个表实际写入的条数。
 */
export async function importSnapshot(input: unknown): Promise<ImportCounts> {
  const snapshot = normalizeSnapshot(input);
  const db = getDb();

  const result: ImportCounts = {
    conversations: snapshot.conversations.length,
    messages: snapshot.messages.length,
    presets: snapshot.presets.length,
    providers: snapshot.providers.length,
    memories: snapshot.memories.length,
  };

  await db.transaction(
    'rw',
    db.conversations,
    db.messages,
    db.presets,
    db.providers,
    db.memories,
    async () => {
      if (snapshot.conversations.length) await db.conversations.bulkPut(snapshot.conversations);
      if (snapshot.messages.length) await db.messages.bulkPut(snapshot.messages);
      if (snapshot.presets.length) await db.presets.bulkPut(snapshot.presets);
      if (snapshot.providers.length) await db.providers.bulkPut(snapshot.providers);
      if (snapshot.memories.length) await db.memories.bulkPut(snapshot.memories);
    },
  );

  return result;
}

/** 从 JSON 字符串导入（幂等）。 */
export async function importDatabaseJson(json: string): Promise<ImportCounts> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    throw new ImportFormatError('导入内容不是合法 JSON');
  }
  return importSnapshot(parsed);
}

/** 清空整库（测试与「重置数据」入口使用）。 */
export async function clearDatabase(): Promise<void> {
  const db = getDb();
  await db.transaction(
    'rw',
    db.conversations,
    db.messages,
    db.presets,
    db.providers,
    db.memories,
    async () => {
      await db.conversations.clear();
      await db.messages.clear();
      await db.presets.clear();
      await db.providers.clear();
      await db.memories.clear();
    },
  );
}
